import { FC, useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress, Chip } from '@mui/material';
import { debounce } from 'lodash';

import { AuthRole } from '@libs/types';
import { Rooftop } from '@libs/models';
import { useLazyRooftopAdminSearchQuery, useLazyRooftopSearchQuery } from '@carcareers/store';
import { useAuth, useForm } from '@carcareers/contexts';

export interface RooftopOption {
  label: string;
  value: number;
  rooftop: Rooftop;
}

interface RooftopPostSelectProps {
  value?: RooftopOption | null;
  onChange?: (rooftop: RooftopOption | null) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  name?: string;
  filterOptions?: (option: RooftopOption) => boolean;
}

export const RooftopPostSelect: FC<RooftopPostSelectProps> = ({
  value,
  onChange,
  label = 'Select Rooftop',
  required = false,
  error = false,
  helperText,
  disabled = false,
  filterOptions: filterOptionsProp,
}) => {
  const { session } = useAuth();
  const isAdmin = session?.user?.role === AuthRole.ADMIN;
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<RooftopOption[]>([]);
  const { data: formData } = useForm();

  const useLazy = isAdmin ? useLazyRooftopAdminSearchQuery : useLazyRooftopSearchQuery;
  const [searchRooftops, { data, isFetching, isError }] = useLazy();

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        searchRooftops({
          id: formData?.employer?.id,
          q: searchTerm,
          page: 1,
          limit: 25,
        });
      }, 300),
    [searchRooftops, formData?.employer?.id],
  );

  useEffect(() => {
    if (open) {
      if (inputValue.trim()) {
        debouncedSearch(inputValue);
      } else {
        searchRooftops({
          id: formData?.employer?.id,
          q: '',
          page: 1,
          limit: 25,
        });
      }
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, open, debouncedSearch, searchRooftops, formData?.employer?.id]);

  useEffect(() => {
    if (data?.items) {
      let rooftopOptions: RooftopOption[] = data.items.map((rooftop) => ({
        label: rooftop.name || rooftop.location || `Rooftop #${rooftop.id}`,
        value: rooftop.id,
        rooftop,
      }));

      if (filterOptionsProp) {
        rooftopOptions = rooftopOptions.filter(filterOptionsProp);
      }

      setOptions(rooftopOptions);
    }
  }, [data, filterOptionsProp]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: RooftopOption | null) => {
    onChange?.(newValue);
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: RooftopOption) => (
    <Box component="li" {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
      <Typography variant="body1" fontWeight="medium">
        {option.label}
      </Typography>
      {option.rooftop.location && option.rooftop.location !== option.label && (
        <Typography variant="body2" color="text.secondary">
          {option.rooftop.location}
        </Typography>
      )}
      {option.rooftop.owner?.user && (
        <Typography variant="body2" color="text.secondary">
          {option.rooftop.owner.user.firstName} {option.rooftop.owner.user.lastName}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
        {(option.rooftop as any).activeJobs !== undefined && (
          <Chip
            label={`${(option.rooftop as any).activeJobs} Active Jobs`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {(option.rooftop as any).totalApplications !== undefined && (
          <Chip
            label={`${(option.rooftop as any).totalApplications} Applications`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      options={options}
      loading={isFetching}
      loadingText="Searching rooftops..."
      noOptionsText={
        isError ? 'Error loading rooftops' : inputValue ? 'No rooftops found' : 'Start typing to search rooftops'
      }
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      filterOptions={(x) => x}
      disabled={disabled}
      renderOption={renderOption}
      sx={{ minWidth: 300 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default RooftopPostSelect;
